import { frac } from '../../shared/scalar';
import type { PlayerState, PropState, SimState } from '../../sim/state';
import { SHADE, SHADE_DEEP, SHADE_SOFT, TAU, WHITE, circle, glow, polygon, rim } from '../paint';
import type { Ctx, Piece } from '../paint';
import type { PixiPalette } from '../palette';
import { REFERENCE_RADIUS } from '../textures-looks';

export const UNIT = 2;

export const CREAM = 0xf6f1e4;
export const POMPOM = 0xfff6dc;
export const BONNET = 0xe8433a;

const HANDS = { along: 12, across: 8 } as const;
const HEAD = 2;
export const POMPOM_AT = -3.5;

function units(draw: (ctx: Ctx) => void): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    ctx.scale(UNIT, UNIT);
    draw(ctx);
    ctx.restore();
  };
}

function torsoPath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, 8.5, 13.5, 0, 0, TAU);
}

function vest(ctx: Ctx): void {
  torsoPath(ctx);
  ctx.fillStyle = '#e4e4e4';
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(1.5, 0, 4.25, 8.9, 0, 0, TAU);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function harlequin(ctx: Ctx): void {
  ctx.save();
  torsoPath(ctx);
  ctx.clip();
  ctx.fillStyle = WHITE;
  for (let row = -3; row <= 3; row += 1) {
    for (let column = -1; column <= 1; column += 1) {
      if ((row + column) % 2 === 0) {
        const x = column * 6;
        const y = row * 5;
        polygon(ctx, [
          [x, y - 2.5],
          [x + 3, y],
          [x, y + 2.5],
          [x - 3, y],
        ]);
        ctx.fill();
      }
    }
  }
  ctx.restore();
  torsoPath(ctx);
  rim(ctx, 1.3);
}

function hands(ctx: Ctx): void {
  for (const side of [-1, 1]) {
    circle(ctx, 3.2, HANDS.along, side * HANDS.across);
    ctx.fillStyle = WHITE;
    ctx.fill();
    ctx.lineWidth = 0.9;
    ctx.strokeStyle = SHADE;
    ctx.stroke();
  }
}

function head(ctx: Ctx): void {
  circle(ctx, 6.6, HEAD, 0);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.1);
}

function bonnet(ctx: Ctx): void {
  circle(ctx, 6, HEAD - 0.4, 0);
  ctx.fillStyle = WHITE;
  ctx.fill();
  ctx.lineWidth = 0.6;
  ctx.strokeStyle = SHADE_SOFT;
  for (let panel = 0; panel < 6; panel += 1) {
    const angle = (panel * TAU) / 6;
    ctx.beginPath();
    ctx.moveTo(HEAD - 0.4, 0);
    ctx.lineTo(HEAD - 0.4 + Math.cos(angle) * 5.6, Math.sin(angle) * 5.6);
    ctx.stroke();
  }
  circle(ctx, 6, HEAD - 0.4, 0);
  rim(ctx, 0.9);
}

function pompom(ctx: Ctx): void {
  circle(ctx, 2.3);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 0.7);
}

function clubHandle(ctx: Ctx): void {
  ctx.lineCap = 'round';
  ctx.strokeStyle = SHADE_DEEP;
  ctx.lineWidth = 3.4;
  ctx.beginPath();
  ctx.moveTo(-6, 0);
  ctx.lineTo(-1, 0);
  ctx.stroke();
  ctx.strokeStyle = WHITE;
  ctx.lineWidth = 1.8;
  ctx.stroke();
}

function clubBody(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(2.5, 0, 4.5, 2.6, 0, 0, TAU);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 0.8);
  ctx.beginPath();
  ctx.ellipse(3.2, -0.9, 2.2, 0.7, 0, 0, TAU);
  ctx.fillStyle = 'rgb(255 255 255 / 0.9)';
  ctx.fill();
  circle(ctx, 1.3, -6.2, 0);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 0.5);
}

function soft(width: number, height: number): Piece {
  return [
    96,
    96,
    REFERENCE_RADIUS,
    (ctx) => {
      glow(ctx, WHITE, 8);
      ctx.fillStyle = WHITE;
      ctx.beginPath();
      ctx.ellipse(0, 0, width, height, 0, 0, TAU);
      ctx.fill();
    },
  ];
}

export const CLUB_PIECES = {
  clubHandle: [40, 16, REFERENCE_RADIUS, units(clubHandle)],
  clubBody: [40, 16, REFERENCE_RADIUS, units(clubBody)],
  clubShadow: soft(12, 6),
} satisfies Record<string, Piece>;

export const JONGLEUR_PIECES = {
  vest: [48, 64, REFERENCE_RADIUS, units(vest)],
  harlequin: [48, 64, REFERENCE_RADIUS, units(harlequin)],
  hands: [64, 48, REFERENCE_RADIUS, units(hands)],
  head: [40, 40, REFERENCE_RADIUS, units(head)],
  bonnet: [40, 40, REFERENCE_RADIUS, units(bonnet)],
  pompom: [16, 16, REFERENCE_RADIUS, units(pompom)],
  shadow: soft(18, 28),
  ...CLUB_PIECES,
} satisfies Record<string, Piece>;

export function clubsOf(state: SimState | undefined, player: Pick<PlayerState, 'id'>): PropState[] {
  const clubs: PropState[] = [];
  for (const prop of state?.props ?? []) {
    if (prop.kind === 'club' && prop.playerId === player.id) {
      clubs.push(prop);
    }
  }
  return clubs;
}

export function clubColor(palette: PixiPalette, classColor: number, index: number): number {
  const colors = [classColor, palette.mage, palette.turquoise];
  return colors[index % colors.length] ?? classColor;
}

export interface Slot {
  along: number;
  across: number;
  turn: number;
}

export function juggleSlot(beats: number, index: number, count: number, slot: Slot): Slot {
  const phase = frac(beats / 2 + index / count);
  slot.along = 17 + 8 * Math.abs(Math.cos(phase * Math.PI));
  slot.across = 9.5 * Math.sin(phase * TAU);
  slot.turn = phase * 2 * TAU;
  return slot;
}
